import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Save, ArrowLeft, Plus, Trash2, RotateCcw, AlertTriangle, Tag, History, UserCheck, ExternalLink,
  CheckCircle2, Loader2, MessageCircle, Mail, Instagram, Link2, FileText, Check, RefreshCw, List, Copy
} from 'lucide-react';
import { pdf } from '@react-pdf/renderer';
import { OfferPDF } from './OfferPDF';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import {
  enumerateNights, nightCount, formatNightLabel, buildNightlyRates, distributeTotal, addDays,
  legacyNightlyRates, sumNightly, round2, ratesCacheFromRows, toAmount, parsePriceInput, MAX_NIGHTS,
  parseDiscountInput, discountUnitMismatch, computeDiscount, discountLabel, formatMoneyTR, DISCOUNT_TYPES, DEFAULT_DISCOUNT_NAME
} from '../../utils/pricing';
import { useSeason } from '../../context/SeasonContext';
import { isValidSeason, offerSeason, ALL_SEASONS } from '../../utils/season';
import { API_URL, jsonAuthHeaders, readJsonOrThrow } from '../../utils/api';
import {
  dateKey, formatDateTR, formatStay, offerMoneyText, offerRoomsText, offerPdfFileName, offerLink, waPhone,
  buildOfferMessage, buildOfferEmail, whatsappUrl, mailtoUrl, instagramUrl
} from '../../utils/offerShare';
import { customerOffers, customerSummary, customerSummaryText } from '../../utils/customer';
import { fetchMessageSettings, logOfferShare } from '../../utils/shareSettings';
import { foldTr, phoneKey, emailKey, instagramKey } from '../../utils/text';

// İndirim adı önerileri (serbest metin de yazılabilir)
const DISCOUNT_NAME_SUGGESTIONS = [DEFAULT_DISCOUNT_NAME, 'Özel İndirim', 'Kurumsal İndirim', 'Grup İndirimi'];
const DISCOUNT_NAME_MAX = 100; // offers.discountName VARCHAR(100)
const formatAmount = formatMoneyTR;

// Görseller /uploads ile başlıyorsa PDF oluşturucu için tam adres gerekir (canlıda '' → aynı alan adı)
const SERVER_URL = API_URL.replace(/\/api$/, '');

const EMPTY_LIST = [];

// Müşteri kartı eşleştirmesi: eski kayıtlarda "-", "yok" gibi dolgu değerleri var; yalnızca gerçek görünen
// e-posta / Instagram adı eşleştirmede kullanılır (telefon için text.js phoneKey en az 7 rakam ister).
const looksLikeEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
const looksLikeInstagram = (value) => /^[a-z0-9._]{3,30}$/.test(instagramKey(value));

// Teklif listesindeki "Kopyala" müşteri adının sonuna " (Kopya)" ekler (kopyanın kopyasında birden çok kez).
const COPY_SUFFIX = /(?:\s*\(Kopya\))+\s*$/;
const isCopyName = (name) => COPY_SUFFIX.test(String(name ?? ''));
const stripCopySuffix = (name) => String(name ?? '').replace(COPY_SUFFIX, '').trim();

const STATUS_BADGE = {
  'Onaylandı': 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  'Onaylanmadı': 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  'Onay Bekliyor': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
};

// Ağ hatası ("Failed to fetch") kullanıcıya Türkçe gösterilsin
const errorText = (error, fallback) => {
  if (error instanceof TypeError && /fetch|network/i.test(error.message || '')) return 'Sunucuya ulaşılamadı, internet bağlantısını kontrol edin.';
  return error?.message || fallback;
};

// PDF blob → base64 (data: öneki olmadan)
const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => {
    const result = typeof reader.result === 'string' ? reader.result.split(',')[1] : '';
    if (result) resolve(result);
    else reject(new Error('PDF dosyası okunamadı.'));
  };
  reader.onerror = () => reject(reader.error || new Error('PDF dosyası okunamadı.'));
  reader.readAsDataURL(blob);
});

// Oda satırı (yalnızca form durumu; kaydedilen biçim handleSubmit içindeki roomsData'dır)
//   adults / childrenPaid (7-12) / childrenFree (0-6) → fiyatı belirleyen doluluk (bkz. utils/pricing.js)
//   nightlyRates → ekranda görünen gecelik döküm = buildNightlyRates(satır) — her değişiklikte yeniden kurulur
//   pinned       → { tarih: {price, source, base?, childTotal?} } sabitlenmiş geceler: elle girilenler ve
//                  kayıtlı tekliften yüklenenler. Takvimden yeniden hesaplanmaz; tarih geçici silinip geri
//                  girilince de korunur. Yetişkin/7-12 çocuk değişince sabit gece varsa kullanıcıya sorulur:
//                  onaylarsa tümü takvimden yeniden hesaplanır, reddederse fiyatlar kalır (occupancyStale).
//   price        → gece kutusunda yazılan HAM değer olabilir ("29.500"); hesaplarda toAmount() kullanılır.
//   rateCache    → { tarih: { kişi: fiyat } } bu oda tipi için takvimden okunan ham fiyatlar.
//                  Takvim okunamazsa önbelleğe yazılmaz → sonraki değişiklikte yeniden sorgulanır.
//   childAgesUnknown → eski teklif: çocuk sayısı kayıtlı ama yaş grubu yok (hepsi 0-6 sayıldı)
//   pricing      → takvimden fiyat bekleniyor (kaydet engellenir)
let lineSeq = 0;
const newLine = (fields = {}) => ({
  lineId: `line-${++lineSeq}`,
  roomId: '', checkIn: '', checkOut: '', adults: 1, childrenPaid: 0, childrenFree: 0, childAgesUnknown: false,
  nightlyRates: [], pinned: {}, rateCache: {}, pricing: false, pricingError: '', occupancyStale: false,
  ...fields,
});

const pinNights = (pinned, nightlyRates) => {
  const next = { ...(pinned || {}) };
  (nightlyRates || []).forEach(n => {
    next[n.date] = { price: n.price, source: n.source, ...(n.childTotal > 0 ? { base: n.base, childTotal: n.childTotal } : {}) };
  });
  return next;
};

// Yalnızca elle girilen sabitler (TL dışında oda değişince ve TL'ye dönünce takvim kaynaklı sabitler atılır)
const onlyManualPins = (pinned) =>
  Object.fromEntries(Object.entries(pinned || {}).filter(([, v]) => v.source === 'manual'));

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);

const ADULT_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);
const CHILD_OPTIONS = Array.from({ length: 9 }, (_, i) => i);

const SOURCE_STYLES = {
  calendar: 'border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-600',
  default: 'border-amber-400 bg-amber-50 dark:bg-amber-900/30 dark:border-amber-600',
  manual: 'border-violet-400 bg-violet-50 dark:bg-violet-900/30 dark:border-violet-500',
  error: 'border-red-500 bg-red-50 dark:bg-red-900/30 dark:border-red-500',
  missing: 'border-red-500 border-dashed bg-red-50 dark:bg-red-900/30 dark:border-red-500',
  pending: 'border-gray-200 bg-gray-100 dark:bg-gray-700 dark:border-gray-600',
};
const SOURCE_TITLES = {
  calendar: 'Fiyat takviminden',
  default: 'Fiyat takviminde bu gece için fiyat yok (tanımlı dönem dışı) — fiyat 0, gerekirse elle girin',
  manual: 'Elle girildi',
  error: 'Fiyat takvimi okunamadı — geçici olarak 0 yazıldı, fiyatı kontrol edin',
  missing: 'Takvimde bu gece için bu yetişkin sayısına fiyat yok — fiyatı elle girin',
  pending: 'Fiyat bekleniyor',
};

const fmt = (n) => Number(n).toLocaleString('tr-TR', { maximumFractionDigits: 2 });

// Gece kutusunun ipucu: "Takvim: 29.500 + çocuk 5.000"
const nightTitle = (night) => {
  const base = SOURCE_TITLES[night.source] || '';
  if (night.source === 'calendar' && night.childTotal > 0) {
    return `${base}: oda ${fmt(night.base)} + çocuk ${fmt(night.childTotal)}`;
  }
  return base;
};

// Oda toplamı: yazarken değil, alan bırakılınca (ya da Enter) gecelere dağıtılır.
// Her tuşta dağıtsaydık "1" yazıldığı an gecelerin oranı bozulurdu.
function LineTotalInput({ total, disabled, onCommit }) {
  const [draft, setDraft] = useState(null);
  const cancelled = useRef(false);
  return (
    <input
      type="text"
      inputMode="decimal"
      autoComplete="off"
      disabled={disabled}
      className="w-full p-2 border rounded dark:bg-gray-600 dark:border-gray-500 dark:text-white text-right font-bold disabled:opacity-50"
      value={draft ?? (total ? String(total) : '')}
      onFocus={() => {
        cancelled.current = false;
        // Pencereden dönülünce yarım kalan taslak korunur
        setDraft(prev => (prev !== null ? prev : (total ? String(total) : '')));
      }}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => {
        // Alt+Tab / sekme değişimi de blur üretir: yarım yazılmış "14" kaydedilmesin
        if (!document.hasFocus()) return;
        // "14.200" → 14200 (type=number olsaydı tarayıcı 14,2 okurdu)
        if (!cancelled.current && draft !== null && draft.trim() !== '') {
          const parsed = parsePriceInput(draft);
          if (!Number.isFinite(parsed) || parsed < 0) alert(`"${draft}" geçerli bir tutar değil. Ör. 14200 ya da 14.200 yazın.`);
          else if (parsed !== total) onCommit(parsed);
        }
        setDraft(null);
      }}
      onKeyDown={e => {
        if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
        if (e.key === 'Escape') { e.preventDefault(); cancelled.current = true; e.currentTarget.blur(); }
      }}
      placeholder="Toplam"
      title="Toplamı değiştirirseniz geceler aynı oranda güncellenir"
    />
  );
}

// Kayıttan sonra açılan "Teklif hazır" penceresi: PDF yüklenince paylaşım düğmeleri açılır.
//   pdfState: 'uploading' | 'ok' | 'error' — PDF müşteriye açılmadan link gitmesin diye 'ok' olana kadar paylaşım kapalı.
//   shareCtx: buildOfferMessage / buildOfferEmail bağlamı { messages, logos, bank, origin }
function OfferReadyPanel({ offer, pdfState, pdfError, onRetryPdf, onExit, shareCtx, token, onShared }) {
  const [flash, setFlash] = useState(null); // { key, text } — "Kopyalandı" geri bildirimi
  const flashTimer = useRef(null);
  useEffect(() => () => clearTimeout(flashTimer.current), []);
  const showFlash = (key, text) => {
    clearTimeout(flashTimer.current);
    setFlash({ key, text });
    flashTimer.current = setTimeout(() => setFlash(null), 2500);
  };

  const pdfOk = pdfState === 'ok';
  const uploading = pdfState === 'uploading';
  const hasPhone = !!waPhone(offer.phone);
  const hasEmail = !!String(offer.email || '').trim();
  const hasInstagram = !!instagramKey(offer.instagram);
  const link = offerLink(offer, shareCtx.origin);

  const shared = (channel) => {
    logOfferShare(offer, channel, token);
    if (onShared) onShared();
  };

  const shareWhatsApp = (kind, channel) => {
    window.open(whatsappUrl(offer.phone, buildOfferMessage(kind, offer, shareCtx)), '_blank', 'noopener');
    shared(channel);
  };

  const shareEmail = () => {
    const { subject, body } = buildOfferEmail(offer, shareCtx);
    window.location.href = mailtoUrl(offer.email, subject, body);
    shared('E-posta');
  };

  // Instagram DM'e metin aktarılamıyor: mesaj panoya kopyalanır, sohbet açılır, kullanıcı yapıştırır
  const shareInstagram = async () => {
    const message = buildOfferMessage('instagram', offer, shareCtx);
    try {
      await navigator.clipboard.writeText(message);
      showFlash('instagram', 'Mesaj kopyalandı — Instagram sohbetine yapıştırın.');
    } catch {
      showFlash('instagram', 'Mesaj panoya kopyalanamadı; Instagram sohbeti açıldı.');
    }
    window.open(instagramUrl(offer.instagram), '_blank', 'noopener');
    shared('Instagram');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      showFlash('link', 'Kopyalandı');
    } catch {
      // Pano erişimi yoksa (izin / güvensiz bağlam) kullanıcı elle kopyalasın
      window.prompt('Linki kopyalayın (Ctrl+C):', link);
    }
    shared('Link kopyalandı');
  };

  const openPdf = () => window.open(link, '_blank', 'noopener');

  const noPdfHint = uploading ? 'PDF hazırlanıyor, birkaç saniye bekleyin.' : "PDF yüklenemedi; önce PDF'i tekrar oluşturun.";
  const btnBase = 'w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
  const missing = [
    !hasPhone && 'WhatsApp: telefon numarası girilmedi',
    !hasEmail && 'E-posta: e-posta adresi girilmedi',
  ].filter(Boolean);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="offer-ready-title"
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto border border-gray-100 dark:border-gray-700"
      >
        <div className="p-5 border-b border-gray-100 dark:border-gray-700 flex items-start gap-3">
          {pdfOk ? <CheckCircle2 size={28} className="text-green-600 dark:text-green-400 shrink-0" />
            : uploading ? <Loader2 size={28} className="text-blue-600 dark:text-blue-400 shrink-0 animate-spin" />
            : <AlertTriangle size={28} className="text-red-600 dark:text-red-400 shrink-0" />}
          <div className="min-w-0">
            <h2 id="offer-ready-title" className="text-lg font-bold text-gray-800 dark:text-white">
              {pdfOk ? 'Teklif hazır' : 'Teklif kaydedildi'}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {pdfOk ? 'Müşteriye göndermek için bir kanal seçin.' : uploading ? 'PDF hazırlanıyor…' : 'PDF yüklenemedi.'}
            </p>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 p-3 text-sm space-y-1">
            <p className="font-semibold text-gray-800 dark:text-white break-words">
              {offer.customerName}
              {offer.id != null && <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">#{offer.id}</span>}
            </p>
            {offerRoomsText(offer) && <p className="text-gray-600 dark:text-gray-300 break-words">{offerRoomsText(offer)}</p>}
            {formatStay(offer) && <p className="text-gray-600 dark:text-gray-300">{formatStay(offer)}</p>}
            <p className="text-base font-bold text-blue-700 dark:text-blue-300">{offerMoneyText(offer)}</p>
          </div>

          {pdfState === 'error' && (
            <div role="alert" className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-700 dark:text-red-300">
              <p className="flex items-start gap-2">
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                <span className="break-words">Teklif kaydedildi ama PDF yüklenemedi: {pdfError}</span>
              </p>
              <button
                type="button"
                onClick={onRetryPdf}
                className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700"
              >
                <RefreshCw size={16} /> PDF'i tekrar oluştur
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button type="button" onClick={() => shareWhatsApp('waDetailed', 'WhatsApp (detaylı)')} disabled={!pdfOk || !hasPhone}
              title={!pdfOk ? noPdfHint : !hasPhone ? 'Telefon numarası girilmedi' : 'Oda, tarih ve tutarla birlikte gönder'}
              className={`${btnBase} bg-green-600 border-green-600 text-white hover:bg-green-700`}>
              <MessageCircle size={18} /> WhatsApp (detaylı)
            </button>
            <button type="button" onClick={() => shareWhatsApp('waLink', 'WhatsApp (link)')} disabled={!pdfOk || !hasPhone}
              title={!pdfOk ? noPdfHint : !hasPhone ? 'Telefon numarası girilmedi' : 'Yalnızca PDF linkini gönder'}
              className={`${btnBase} bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40`}>
              <MessageCircle size={18} /> WhatsApp (link)
            </button>
            <button type="button" onClick={shareEmail} disabled={!pdfOk || !hasEmail}
              title={!pdfOk ? noPdfHint : !hasEmail ? 'E-posta adresi girilmedi' : 'E-posta ile gönder'}
              className={`${btnBase} bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/40`}>
              <Mail size={18} /> E-posta
            </button>
            {hasInstagram && (
              <button type="button" onClick={shareInstagram} disabled={!pdfOk}
                title={!pdfOk ? noPdfHint : 'Mesajı kopyala ve Instagram sohbetini aç'}
                className={`${btnBase} bg-pink-50 dark:bg-pink-900/20 border-pink-200 dark:border-pink-800 text-pink-700 dark:text-pink-300 hover:bg-pink-100 dark:hover:bg-pink-900/40`}>
                <Instagram size={18} /> Instagram
              </button>
            )}
            <button type="button" onClick={copyLink} disabled={!pdfOk}
              title={!pdfOk ? noPdfHint : 'Teklif PDF linkini panoya kopyala'}
              className={`${btnBase} bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600`}>
              {flash?.key === 'link' ? <><Check size={18} className="text-green-600 dark:text-green-400" /> Kopyalandı</> : <><Link2 size={18} /> Linki kopyala</>}
            </button>
            <button type="button" onClick={openPdf} disabled={!pdfOk}
              title={!pdfOk ? noPdfHint : "PDF'i yeni sekmede aç"}
              className={`${btnBase} bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40`}>
              <FileText size={18} /> PDF'i aç <ExternalLink size={14} className="opacity-70" />
            </button>
          </div>

          {flash && flash.key !== 'link' && (
            <p role="status" className="text-sm text-green-700 dark:text-green-400 flex items-center gap-1.5">
              <Check size={16} className="shrink-0" /> {flash.text}
            </p>
          )}
          {missing.length > 0 && (
            <ul className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
              {missing.map(m => <li key={m}>{m} — düğme pasif.</li>)}
            </ul>
          )}
        </div>

        <div className="p-5 pt-0">
          <button type="button" onClick={onExit} disabled={uploading}
            title={uploading ? 'PDF yüklemesi bitince listeye dönebilirsiniz' : undefined}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed">
            <List size={18} /> Teklif listesine dön
          </button>
        </div>
      </div>
    </div>
  );
}

// Rota /offers/new → /offers/edit/7 (ya da bir tekliften diğerine) değişince React aynı bileşeni yeniden kullanır;
// önceki formun durumu (indirim, kayıt paneli…) yeni teklife taşınmasın diye form teklif no'suna göre yeniden kurulur.
export default function OfferForm() {
  const { id } = useParams();
  return <OfferFormPage key={id || 'new'} />;
}

function OfferFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentUser } = useAuth();
  const { data: store, fetchData, refreshData, incrementReads, incrementWrites } = useData();
  const canSeeMoney = !!currentUser?.permissions?.view_financials;
  // Kaydedilmemiş değişiklik (kullanıcı bir alanı değiştirince) → sekme kapatılırken ve teklif değiştirirken uyarı
  const [dirty, setDirty] = useState(false);
  const markDirty = () => setDirty(true);
  // Düzenlemede teklif yüklenene kadar kaydet kapalı: boş form kayıtlı teklifin üzerine yazılmasın
  const [offerReady, setOfferReady] = useState(!id);
  const [loadError, setLoadError] = useState('');
  const [lookupWarning, setLookupWarning] = useState(''); // oda tipi / kaynak / logo / banka okunamadı
  const [saveError, setSaveError] = useState('');
  const savingRef = useRef(false);
  // Kayıt sonrası panel: { offer, pdf: 'uploading'|'ok'|'error', pdfError }
  const [ready, setReady] = useState(null);
  const [messageSettings, setMessageSettings] = useState({});
  const { activeSeason, selectedSeason, setSelectedSeason } = useSeason();
  // Düzenlemede teklifin kayıtlı sezonu (başlıkta gösterilir, değiştirilmez)
  const [offerSavedSeason, setOfferSavedSeason] = useState('');
  // Düzenleme sayfası listeden kopyalanmış bir teklifle açıldı (yüklenen müşteri adı " (Kopya)" ile bitiyor)
  const [openedAsCopy, setOpenedAsCopy] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [roomTypes, setRoomTypes] = useState([]);
  const [offerSources, setOfferSources] = useState([]);

  const [customerName, setCustomerName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [instagram, setInstagram] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedSourceId, setSelectedSourceId] = useState('');
  const [currency, setCurrency] = useState('TL');
  const [selectedRooms, setSelectedRooms] = useState(() => [newLine()]);
  // Teklif indirimi (teklif başına tek; tüm odaların toplamına uygulanır — bkz. utils/pricing.js)
  //   discountInput → kullanıcının yazdığı ham değer ("10", "12,5", "5.000")
  //   hadDiscount   → düzenlenen teklifte kayıtlı indirim alanı vardı; indirim kapatılırsa kolonlar null'lanır
  const [discountEnabled, setDiscountEnabled] = useState(false);
  const [discountName, setDiscountName] = useState(DEFAULT_DISCOUNT_NAME);
  const [discountType, setDiscountType] = useState('percentage');
  const [discountInput, setDiscountInput] = useState('');
  const [hadDiscount, setHadDiscount] = useState(false);
  const [settings, setSettings] = useState({});
  // Satır başına son fiyat isteğinin sıra no'su — geç gelen eski yanıtlar yok sayılır
  const pricingSeq = useRef({});
  // Asenkron fiyat yanıtı işlenirken GÜNCEL oda tipleri ve para birimi kullanılsın diye
  // (istek başladığı andaki closure değil — ör. oda tipleri henüz yüklenmemişken başlayan istek)
  const roomTypesRef = useRef(roomTypes);
  roomTypesRef.current = roomTypes;
  const currencyRef = useRef(currency);
  currencyRef.current = currency;

  useEffect(() => {
    // Yanıt başarısızsa hata gövdesi ({message}) veri yerine kullanılmasın
    const jsonOr = async (res, fallback) => {
      if (!res.ok) return fallback;
      try { return await res.json(); } catch { return fallback; }
    };
    const loadLookups = async () => {
      try {
        const [roomsRes, sourcesRes, logosRes, bankRes] = await Promise.all([
            fetch(`${API_URL}/roomTypes`),
            fetch(`${API_URL}/offerSources`),
            fetch(`${API_URL}/settings/logos`),
            fetch(`${API_URL}/settings/bank`)
        ]);
        const roomsData = await jsonOr(roomsRes, []);
        const sourcesData = await jsonOr(sourcesRes, []);
        const logosRaw = await jsonOr(logosRes, {});
        const logosData = logosRaw && typeof logosRaw === 'object' ? { ...logosRaw } : {};

        // Resim URL'lerini düzelt (PDF oluşturucu için tam URL gerekli)
        ['light', 'dark', 'favicon', 'pdfBackground', 'loginImage'].forEach(key => {
            if (typeof logosData[key] === 'string' && logosData[key].startsWith('/uploads')) {
                logosData[key] = SERVER_URL + logosData[key];
            }
        });

        const bankData = await jsonOr(bankRes, {});
        incrementReads(4);
        setRoomTypes(Array.isArray(roomsData) ? roomsData : []);
        setOfferSources(Array.isArray(sourcesData) ? sourcesData : []);
        setSettings({ ...logosData, bank: bankData && typeof bankData === 'object' ? bankData : {} });
        const failed = [
          (!roomsRes.ok || !Array.isArray(roomsData)) && 'oda tipleri',
          (!sourcesRes.ok || !Array.isArray(sourcesData)) && 'teklif kaynakları',
          !logosRes.ok && 'logo/PDF ayarları',
          !bankRes.ok && 'banka bilgileri',
        ].filter(Boolean);
        setLookupWarning(failed.length > 0 ? `Şunlar alınamadı: ${failed.join(', ')}. PDF eksik çıkabilir; sayfayı yenileyip tekrar deneyin.` : '');
      } catch (error) {
        console.error("Veri çekme hatası:", error);
        setLookupWarning('Oda tipleri ve ayarlar alınamadı (sunucuya ulaşılamadı). Sayfayı yenileyip tekrar deneyin.');
      }
    };
    loadLookups();

    if (id) {
      const fetchOffer = async () => {
        let data;
        try {
          const response = await fetch(`${API_URL}/offers/${id}`);
          incrementReads(1);
          if (response.status === 404) throw new Error('Teklif bulunamadı; silinmiş olabilir.');
          data = await readJsonOrThrow(response, 'Teklif yüklenemedi.');
          if (!data || typeof data !== 'object') throw new Error('Teklif yüklenemedi.');
        } catch (error) {
          console.error('Teklif yükleme hatası:', error);
          setLoadError(errorText(error, 'Teklif yüklenemedi.'));
          return;
        }
        setCustomerName(data.customerName);
        setOpenedAsCopy(isCopyName(data.customerName));
        setPhone(data.phone || '');
        setEmail(data.email || '');
        setInstagram(data.instagram || '');
        setNotes(data.notes || '');
        setSelectedSourceId(data.source?.id || '');
        setCurrency(data.currency || 'TL');
        setOfferSavedSeason(offerSeason(data));
        // İndirim: DECIMAL kolonlar string gelir ("10.00"); kutuya Türkçe biçimde yazılır ("5.000", "12,5")
        const savedValue = Number(data.discountValue);
        setHadDiscount(['discountName', 'discountType', 'discountValue', 'discountAmount'].some(k => data[k] != null));
        if (DISCOUNT_TYPES.includes(data.discountType) && savedValue > 0) {
          setDiscountEnabled(true);
          setDiscountName(data.discountName || DEFAULT_DISCOUNT_NAME);
          setDiscountType(data.discountType);
          setDiscountInput(formatAmount(savedValue));
        }
        // Kayıtlı fiyatlar olduğu gibi yüklenir; takvimden YENİDEN HESAPLANMAZ.
        // (Kullanıcı oda/tarih değiştirir ya da "Takvimden yeniden hesapla"ya basarsa hesaplanır.)
        const toLine = (r, fallbackIn, fallbackOut) => {
          // Teklif düzeyindeki tarih API'den ISO zaman damgası gelir ve bir gün geri kaymıştır
          // ("2026-12-24T21:00:00.000Z" = 25 Aralık) → İstanbul gününe çevrilir; oda tarihleri zaten 'YYYY-MM-DD'
          const checkIn = dateKey(r.checkIn || fallbackIn);
          const checkOut = dateKey(r.checkOut || fallbackOut);
          const nightlyRates = legacyNightlyRates({ ...r, checkIn, checkOut });
          // Eski tekliflerde yalnızca toplam çocuk sayısı var → 0-6 yaş sayılır, kullanıcı uyarılır
          const hasSplit = r.childrenFree !== undefined || r.childrenPaid !== undefined;
          const legacyChildren = Number(r.children) || 0;
          return newLine({
            roomId: String(r.id || r.roomId || ''), // Handle potential naming differences
            checkIn, checkOut,
            adults: Math.max(1, Number(r.adults) || 1),
            childrenPaid: hasSplit ? Number(r.childrenPaid) || 0 : 0,
            childrenFree: hasSplit ? Number(r.childrenFree) || 0 : legacyChildren,
            childAgesUnknown: !hasSplit && legacyChildren > 0,
            nightlyRates,
            pinned: pinNights({}, nightlyRates),
          });
        };
        if (data.rooms && Array.isArray(data.rooms)) {
          setSelectedRooms(data.rooms.map(r => toLine(r, data.checkIn, data.checkOut)));
        } else if (data.room) {
          setSelectedRooms([toLine(
            { ...data.room, adults: data.adults, children: data.children },
            data.checkIn, data.checkOut
          )]);
        }
        setOfferReady(true);
      };
      fetchOffer();
    }
  }, [id, incrementReads]);

  // Paylaşım mesajı şablonları (Ayarlar > Mesaj Şablonları); alınamazsa {} → varsayılan şablonlar
  useEffect(() => {
    let alive = true;
    fetchMessageSettings().then(m => { if (alive) setMessageSettings(m || {}); });
    incrementReads(1);
    return () => { alive = false; };
  }, [incrementReads]);

  // Müşteri kartı için tüm teklifler (ortak önbellek; zaten yüklüyse yeniden istenmez).
  // Yalnızca bir kez denenir: istek başarısız olursa döngüye girip sunucuyu yormasın.
  const offersRequested = useRef(false);
  useEffect(() => {
    if (store.offers || offersRequested.current) return;
    offersRequested.current = true;
    fetchData('offers');
  }, [store.offers, fetchData]);

  // Kaydedilmemiş değişiklik varken sekme kapatılır/yenilenirse tarayıcı uyarsın.
  // "Teklif hazır" paneli açıkken uyarı yok (teklif kaydedildi; e-posta düğmesi mailto: ile sayfadan "çıkar").
  useEffect(() => {
    if (!dirty || ready) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty, ready]);

  // ── Müşteri kartı: aynı telefon / e-posta / Instagram ile verilmiş önceki teklifler ──────────────
  const allOffers = Array.isArray(store.offers) ? store.offers : EMPTY_LIST;
  const excludeId = id || ready?.offer?.id;
  const customer = useMemo(() => {
    const who = {
      phone,
      email: looksLikeEmail(email) ? email : '',
      instagram: looksLikeInstagram(instagram) ? instagram : '',
    };
    const list = customerOffers(allOffers, who, { excludeId });
    if (list.length === 0) return null;
    const p = phoneKey(who.phone);
    const e = emailKey(who.email);
    const i = instagramKey(who.instagram);
    const via = [
      p && list.some(o => phoneKey(o.phone) === p) && 'telefon',
      e && list.some(o => emailKey(o.email) === e) && 'e-posta',
      i && list.some(o => instagramKey(o.instagram) === i) && 'Instagram',
    ].filter(Boolean);
    return {
      list,
      summary: customerSummary(list),
      via,
      pending: list.find(o => o.status === 'Onay Bekliyor') || null,
    };
  }, [allOffers, phone, email, instagram, excludeId]);

  // "Bilgileri doldur": en yeni tekliften YALNIZCA boş alanlara yazılır.
  // Eski kayıtlardaki dolgular ("-", "yok") aktarılmaz: e-posta / Instagram yalnızca geçerli biçimdeyse,
  // telefon yalnızca gerçek numaraysa (phoneKey) yazılır — yoksa ör. '-' type="email" alanına düşüp kaydı kilitler.
  // Ad kopya teklifinden geliyorsa " (Kopya)" eki atılır.
  const fillCandidates = (() => {
    const last = customer?.summary.last;
    if (!last) return [];
    const has = (v) => String(v ?? '').trim() !== '';
    return [
      ['ad', customerName, stripCopySuffix(last.customerName), setCustomerName],
      ['telefon', phone, phoneKey(last.phone) ? last.phone : '', setPhone],
      ['e-posta', email, looksLikeEmail(last.email) ? last.email : '', setEmail],
      ['Instagram', instagram, looksLikeInstagram(last.instagram) ? last.instagram : '', setInstagram],
    ].filter(([, current, value]) => !has(current) && has(value));
  })();

  const handleFillFromLast = () => {
    if (fillCandidates.length === 0) return;
    fillCandidates.forEach(([, , value, set]) => set(String(value).trim()));
    markDirty();
  };

  const handleOpenPending = () => {
    const target = customer?.pending;
    if (!target) return;
    if (dirty && !window.confirm('Bu formda kaydedilmemiş değişiklikler var. Bekleyen teklife geçerseniz kaybolur. Devam edilsin mi?')) return;
    navigate(`/offers/edit/${target.id}`);
  };

  const handleBack = () => {
    if (dirty && !ready && !window.confirm('Kaydedilmemiş değişiklikler var. Çıkmak istediğinize emin misiniz?')) return;
    navigate('/offers');
  };

  // Oda tipleri (çocuk ücreti) takvim yanıtından SONRA gelirse satırlar çocuk ücretsiz hesaplanmış olabilir → yeniden kur
  useEffect(() => {
    if (!Array.isArray(roomTypes) || roomTypes.length === 0) return;
    setSelectedRooms(prev => prev.map(l => (l.pricing ? l : { ...l, nightlyRates: computeNightly(l) })));
    // computeNightly her render'da yeniden tanımlanır ama ref'lerden okur; yalnızca roomTypes değişince çalışmalı
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomTypes]);

  // UTC ile hesaplar ve 2000–2100 dışındaki yılları reddeder (yıl yazılırken oluşan '0002-..' ara değerleri)
  const calculateNights = (start, end) => nightCount(start, end);

  const handleAddRoom = () => {
    setSelectedRooms(prev => [...prev, newLine()]);
    markDirty();
  };

  const handleRemoveRoom = (index) => {
    const lineId = selectedRooms[index]?.lineId;
    setSelectedRooms(prev => prev.filter(l => l.lineId !== lineId));
    markDirty();
  };

  // Tüm satır güncellemeleri state'in GÜNCEL hâli (prev) üzerinden yapılır; render anındaki kopyayla
  // satırı değiştirmek, arada gelen bir fiyat yanıtını ezebilirdi.
  const updateLine = (lineId, fn) => {
    setSelectedRooms(prev => prev.map(l => (l.lineId === lineId ? fn(l) : l)));
  };

  // Fiyat takvimi TL cinsinden; başka para biriminde yeni gecelere otomatik fiyat yazılmaz
  const autoPricing = currency === 'TL';

  // Satırın gece dökümünü kurar (saf). Güncel oda tipleri ve para birimi ref'lerden okunur.
  const computeNightly = (line, rateError = false) => {
    const roomType = roomTypesRef.current.find(r => r.id === Number(line.roomId));
    return buildNightlyRates({
      nights: enumerateNights(line.checkIn, line.checkOut),
      rateCache: line.rateCache,
      persons: Number(line.adults) || 1,
      childrenPaid: Number(line.childrenPaid) || 0,
      childFee: roomType?.childFee,
      pinned: line.pinned,
      autoPricing: currencyRef.current === 'TL',
      rateError,
    });
  };

  // Satırın fiyatlarını kurar. Sabitlenmemiş ve önbellekte olmayan geceler için takvim sorgulanır.
  const repriceLine = async (line) => {
    const { lineId } = line;
    const seq = (pricingSeq.current[lineId] || 0) + 1;
    pricingSeq.current[lineId] = seq;

    const count = nightCount(line.checkIn, line.checkOut);
    if (count > MAX_NIGHTS) {
      updateLine(lineId, l => ({ ...l, nightlyRates: [], pricing: false,
        pricingError: `Konaklama en fazla ${MAX_NIGHTS} gece olabilir (seçilen: ${count.toLocaleString('tr-TR')} gece). Tarihleri kontrol edin.` }));
      return;
    }
    const nights = enumerateNights(line.checkIn, line.checkOut);
    if (!line.roomId || nights.length === 0) {
      updateLine(lineId, l => ({ ...l, nightlyRates: [], pricing: false, pricingError: '' }));
      return;
    }

    const toFetch = currencyRef.current === 'TL'
      ? nights.filter(d => !line.pinned?.[d] && !hasOwn(line.rateCache, d))
      : [];
    if (toFetch.length === 0) {
      updateLine(lineId, l => ({ ...l, nightlyRates: computeNightly(l), pricing: false, pricingError: '' }));
      return;
    }

    const from = toFetch[0];
    const to = toFetch[toFetch.length - 1];
    updateLine(lineId, l => ({ ...l, nightlyRates: computeNightly(l), pricing: true, pricingError: '' }));
    let fetched = null;
    let rateError = '';
    try {
      const res = await fetch(
        `${API_URL}/roomRates?from=${from}&to=${to}&roomTypeId=${Number(line.roomId)}`,
        { headers: { 'Authorization': `Bearer ${currentUser?.token}` } }
      );
      const rows = await res.json().catch(() => null);
      if (!res.ok || !Array.isArray(rows)) throw new Error(rows?.message || `HTTP ${res.status}`);
      // Aralıktaki her gece "sorgulandı" sayılır; satırı olmayan gece {} (takvimde fiyat yok)
      fetched = ratesCacheFromRows(rows, enumerateNights(from, addDays(to, 1)));
    } catch (err) {
      rateError = `Fiyat takvimi okunamadı (${err.message}). Kırmızı gecelere geçici olarak 0 yazıldı — fiyatları elle girin ya da "Takvimden yeniden hesapla"ya basın.`;
    }

    if (pricingSeq.current[lineId] !== seq) return; // bu arada oda/tarih/doluluk yine değişti
    updateLine(lineId, l => {
      if (String(l.roomId) !== String(line.roomId)) return l; // yanıt başka bir odaya ait
      const next = fetched ? { ...l, rateCache: { ...l.rateCache, ...fetched } } : l;
      return { ...next, nightlyRates: computeNightly(next, !!rateError), pricing: false, pricingError: rateError };
    });
  };

  const handleRoomChange = (index, field, value) => {
    const current = selectedRooms[index];
    if (!current) return;
    let patch = { [field]: value };

    if (field === 'roomId') {
      // TL'de oda değişince o odaya elle girilen fiyatlar yeni odanın takvim fiyatlarıyla değişir → onay al.
      // TL dışında takvim yok; elle girilen fiyatlar yeni odaya aynen taşınır.
      if (autoPricing && current.roomId && Object.values(current.pinned).some(p => p.source === 'manual') &&
          !window.confirm('Oda tipi değişince bu odaya elle girdiğiniz gece fiyatları silinip yeni odanın takvim fiyatları gelecek. Devam edilsin mi?')) {
        return; // kontrollü select eski değerine döner
      }
      patch = { ...patch, rateCache: {}, pinned: autoPricing ? {} : onlyManualPins(current.pinned), occupancyStale: false };
    }

    if (field === 'adults' || field === 'childrenPaid') {
      // Yetişkin ve 7-12 yaş çocuk fiyatı değiştirir. Sabit (kayıtlı ya da elle girilmiş) gece varsa sor:
      // sessizce eski fiyatta bırakmak müşteriye eksik fiyat gitmesine yol açıyordu.
      let pinned = current.pinned;
      let occupancyStale = current.occupancyStale;
      if (Object.keys(current.pinned || {}).length > 0) {
        if (autoPricing && window.confirm('Kişi sayısı değişti.\n\nGece fiyatları Fiyat Takvimi\'nden yeniden hesaplansın mı? Kayıtlı ya da elle girilmiş gece fiyatları silinir.\n\n(İptal: mevcut fiyatlar aynen kalır; kişi/çocuk farkını elle girmeniz gerekir.)')) {
          pinned = {};
          occupancyStale = false;
        } else {
          occupancyStale = true;
        }
      }
      patch = { ...patch, [field]: Number(value), pinned, occupancyStale, ...(field === 'childrenPaid' ? { childAgesUnknown: false } : {}) };
    }
    if (field === 'childrenFree') {
      patch = { ...patch, childrenFree: Number(value), childAgesUnknown: false };
    }

    updateLine(current.lineId, l => ({ ...l, ...patch }));
    if (field !== 'childrenFree') repriceLine({ ...current, ...patch });
  };

  const handleNightPriceChange = (index, date, value) => {
    const lineId = selectedRooms[index]?.lineId;
    updateLine(lineId, l => ({
      ...l,
      nightlyRates: l.nightlyRates.map(n => (n.date === date ? { date, price: value, source: 'manual' } : n)),
      pinned: { ...l.pinned, [date]: { price: value, source: 'manual' } },
    }));
  };

  const handleLineTotalCommit = (index, total) => {
    const line = selectedRooms[index];
    if (!line) return;
    const current = sumNightly(line.nightlyRates);
    if (!Number.isFinite(total) || total < 0) return;
    // Yarım yazılmış ya da hatalı değer (ör. 14.200 yerine 14) gece fiyatlarını kalıcı bozmasın
    if (current > 0 && total < current * 0.1 &&
        !window.confirm(`Oda toplamı ${current.toLocaleString('tr-TR')} → ${total.toLocaleString('tr-TR')} ${currency} olarak değişecek. Emin misiniz?`)) return;
    updateLine(line.lineId, l => {
      const nightlyRates = distributeTotal(l.nightlyRates, total).map(n => ({ date: n.date, price: n.price, source: 'manual' }));
      return { ...l, nightlyRates, pinned: pinNights(l.pinned, nightlyRates) };
    });
  };

  const handleRecalculate = (index) => {
    const line = selectedRooms[index];
    if (!line) return;
    if (Object.values(line.pinned).some(p => p.source === 'manual') &&
        !window.confirm('Bu odadaki elle girilmiş fiyatlar silinip fiyat takviminden yeniden hesaplanacak. Devam edilsin mi?')) return;
    const patch = { pinned: {}, rateCache: {}, occupancyStale: false }; // önbellek de boşalır: takvimdeki son değişiklikler gelsin
    markDirty();
    updateLine(line.lineId, l => ({ ...l, ...patch }));
    repriceLine({ ...line, ...patch });
  };

  // TL'den başka para birimine geçerken takvimden gelen TL fiyatlar sessizce kalmasın
  const handleCurrencyChange = (next) => {
    const leavingTl = currency === 'TL' && next !== 'TL';
    const hasTlPrices = selectedRooms.some(r => r.nightlyRates.some(n => n.source !== 'manual' && n.price !== ''));
    let reset = false;
    if (leavingTl && hasTlPrices) {
      reset = window.confirm(`Fiyat takviminden gelen fiyatlar TL cinsinden. ${next} için bu geceler sıfırlansın mı?\n\n(İptal: fiyatlar olduğu gibi kalır, sonra elle güncellersiniz.)`);
    }
    // TL dışında sabitlenmemiş geceler 0 olur; mevcut rakamları korumak için (iptalde) hepsi sabitlenir
    const returningToTl = currency !== 'TL' && next === 'TL';
    const nextLines = returningToTl
      ? selectedRooms.map(l => ({ ...l, pinned: onlyManualPins(l.pinned) }))
      : !leavingTl ? selectedRooms : selectedRooms.map(l => {
      const pinned = { ...l.pinned };
      l.nightlyRates.forEach(n => {
        if (pinned[n.date]) return;
        pinned[n.date] = (reset || n.price === '') ? { price: 0, source: 'manual' } : { price: n.price, source: n.source };
      });
      if (reset) Object.keys(pinned).forEach(d => { if (pinned[d].source !== 'manual') pinned[d] = { price: 0, source: 'manual' }; });
      return { ...l, pinned };
    });
    if (leavingTl || returningToTl) {
      setSelectedRooms(prev => prev.map(p => {
        const n = nextLines.find(x => x.lineId === p.lineId);
        return n ? { ...p, pinned: n.pinned } : p;
      }));
    }
    setCurrency(next);
    currencyRef.current = next; // repriceLine aşağıda yeni para birimini okusun
    nextLines.forEach(l => repriceLine(l));
  };

  const calculateSubTotal = () => {
    return round2(selectedRooms.reduce((total, room) => total + sumNightly(room.nightlyRates), 0));
  };

  // İndirim durumu: { amount, total, value, error }. Hata varken indirim uygulanmaz (toplam = ara toplam).
  const calculateDiscount = () => {
    const subTotal = calculateSubTotal();
    if (!discountEnabled) return { amount: 0, total: subTotal, value: 0, error: '' };
    const value = round2(parseDiscountInput(discountInput, discountType));
    let error = '';
    if (!discountName.trim()) error = 'İndirim adını girin.';
    else if (String(discountInput).trim() === '') error = 'İndirim değerini girin.';
    else if (discountUnitMismatch(discountInput, discountType)) error = discountType === 'amount'
      ? `"Tutar" seçili ama değerde % var. Yüzde indirim için "Yüzde (%)"yi seçin.`
      : `"Yüzde" seçili ama değerde TL var. Tutar indirimi için "Tutar"ı seçin.`;
    else if (!Number.isFinite(value) || value <= 0) error = `Geçersiz değer: "${discountInput}". Ör. ${discountType === 'percentage' ? '10 ya da 12,5' : '5000 ya da 5.000'}`;
    else if (discountType === 'percentage' && value > 100) error = "Yüzde 100'den büyük olamaz.";
    else if (discountType === 'amount' && value > subTotal) error = `İndirim tutarı ara toplamdan (${formatAmount(subTotal)} ${currency}) büyük olamaz.`;
    if (error) return { amount: 0, total: subTotal, value: 0, error };
    return { ...computeDiscount({ subTotal, type: discountType, value }), value, error: '' };
  };

  const calculateTotalPrice = () => {
    return calculateDiscount().total;
  };

  // PDF'i üretir ve sunucuya yükler; başarısızsa hata fırlatır (yanıt kontrol edilir)
  const generateAndUploadPdf = async (offer) => {
    const blob = await pdf(<OfferPDF offer={offer} settings={settings} />).toBlob();
    const pdfBase64 = await blobToBase64(blob);
    const response = await fetch(`${API_URL}/offers/${offer.id}/pdf`, {
      method: 'POST',
      headers: jsonAuthHeaders(currentUser?.token),
      body: JSON.stringify({ pdfBase64, fileName: offerPdfFileName(offer) }),
    });
    await readJsonOrThrow(response, 'PDF sunucuya yüklenemedi.');
    incrementWrites(1);
  };

  // Paneldeki PDF adımı (ilk deneme ve "PDF'i tekrar oluştur")
  const runPdfStep = async (offer) => {
    setReady(r => (r ? { ...r, pdf: 'uploading', pdfError: '' } : r));
    try {
      await generateAndUploadPdf(offer);
      setReady(r => (r ? { ...r, pdf: 'ok', pdfError: '' } : r));
    } catch (error) {
      console.error('PDF oluşturma/yükleme hatası:', error);
      setReady(r => (r ? { ...r, pdf: 'error', pdfError: errorText(error, 'bilinmeyen hata') } : r));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Kayıt sürerken ya da kaydedildikten sonra ikinci kez gönderilmesin (mükerrer teklif)
    if (savingRef.current || ready || !offerReady) return;
    setSaveError('');

    // Validasyon: Her odanın geçerli bir süresi olmalı
    const hasInvalidRoom = selectedRooms.some(r => calculateNights(r.checkIn, r.checkOut) <= 0);
    if (hasInvalidRoom) return alert("Lütfen tüm odalar için geçerli giriş ve çıkış tarihleri seçiniz.");
    if (!selectedSourceId) return alert("Lütfen bir kaynak seçiniz.");

    // Fiyat dökümü: takvim yanıtı beklenirken ya da döküm tarihlerle uyuşmazken kaydetme
    if (selectedRooms.some(r => calculateNights(r.checkIn, r.checkOut) > MAX_NIGHTS)) return alert(`Bir oda için konaklama en fazla ${MAX_NIGHTS} gece olabilir. Lütfen tarihleri kontrol edin.`);
    if (selectedRooms.some(r => r.pricing)) return alert("Fiyatlar fiyat takviminden alınıyor, lütfen birkaç saniye bekleyip tekrar deneyin.");
    const mismatch = selectedRooms.findIndex(r => {
      const nights = enumerateNights(r.checkIn, r.checkOut);
      return nights.length !== r.nightlyRates.length || nights.some((d, i) => r.nightlyRates[i]?.date !== d);
    });
    if (mismatch !== -1) return alert(`${mismatch + 1}. odanın gecelik fiyatları tarihlerle uyuşmuyor. Lütfen o odada "Takvimden yeniden hesapla"ya basın.`);
    const missingNights = selectedRooms.reduce((c, r) => c + r.nightlyRates.filter(n => n.source === 'missing').length, 0);
    if (missingNights > 0) return alert(`${missingNights} gece için fiyat takviminde seçilen yetişkin sayısına fiyat yok (kesik kırmızı kutular). Lütfen bu gecelerin fiyatını elle girin ya da yetişkin sayısını kontrol edin.`);
    const badPrice = selectedRooms.some(r => r.nightlyRates.some(n => !Number.isFinite(toAmount(n.price)) || toAmount(n.price) < 0));
    if (badPrice) return alert("Boş ya da geçersiz gecelik fiyat var. Lütfen tüm gecelere fiyat girin (ör. 29500 ya da 29.500).");
    // İndirim hatası kesin engeldir → aşağıdaki onay sorularından ÖNCE (onaylar verildikten sonra iptal olmasın)
    const discount = calculateDiscount();
    if (discountEnabled && discount.error) return alert(`İndirim: ${discount.error}\n\nİndirim uygulanmayacaksa "İndirim uygula" kutusunun işaretini kaldırın.`);
    const overCapacity = selectedRooms
      .map(r => ({ r, room: roomTypes.find(t => t.id === Number(r.roomId)) }))
      .filter(({ r, room }) => room?.adultCapacity && Number(r.adults) > Number(room.adultCapacity));
    if (overCapacity.length > 0 && !window.confirm(
      overCapacity.map(({ r, room }) => `${room.name}: ${r.adults} yetişkin (kapasite ${room.adultCapacity})`).join('\n') +
      '\n\nKapasitenin üzerinde yetişkin var. Yine de kaydedilsin mi?'
    )) return;
    const zeroNights = selectedRooms.reduce((c, r) => c + r.nightlyRates.filter(n => toAmount(n.price) === 0).length, 0);
    if (zeroNights > 0 && !window.confirm(`${zeroNights} gecenin fiyatı 0. Yine de kaydedilsin mi?`)) return;
    const errorNights = selectedRooms.reduce((c, r) => c + r.nightlyRates.filter(n => n.source === 'error').length, 0);
    if (errorNights > 0 && !window.confirm(`${errorNights} gece için fiyat takvimi okunamadı ve fiyat 0 yazıldı (kırmızı geceler). Bu fiyatlarla kaydedilsin mi?`)) return;
    if (currency !== 'TL') {
      const tlNights = selectedRooms.reduce((c, r) => c + r.nightlyRates.filter(n => n.source !== 'manual').length, 0);
      if (tlNights > 0 && !window.confirm(`Bu ${currency} teklifte ${tlNights} gecenin fiyatı TL fiyat takviminden geldi ve TL rakamı olarak duruyor. Yine de kaydedilsin mi?`)) return;
    }
    if (discountEnabled) {
      // Yazım hatası yakalama (ör. %5 yerine 50, 5.000 yerine 50.000)
      const subTotal = calculateSubTotal();
      if (subTotal > 0 && discount.amount / subTotal >= 0.5 && !window.confirm(
        `İndirim ara toplamın %${Math.round((discount.amount / subTotal) * 100)}'i: -${formatAmount(discount.amount)} ${currency}. ` +
        `Teklif toplamı ${formatAmount(discount.total)} ${currency} olacak. Doğru mu?`
      )) return;
    }

    // Düğme kayıt bitene kadar kilitli; kayıt başarılıysa panel açılır ve form bir daha gönderilmez
    savingRef.current = true;
    setLoading(true);
    let savedOffer = null;
    try {
        // Genel tarihleri odalardan hesapla (En erken giriş, en geç çıkış)
        const validDates = selectedRooms.filter(r => r.checkIn && r.checkOut);
        const globalCheckIn = validDates.length > 0 ? validDates.reduce((min, r) => r.checkIn < min ? r.checkIn : min, validDates[0].checkIn) : '';
        const globalCheckOut = validDates.length > 0 ? validDates.reduce((max, r) => r.checkOut > max ? r.checkOut : max, validDates[0].checkOut) : '';
        const globalNights = calculateNights(globalCheckIn, globalCheckOut);

        const roomsData = selectedRooms.map(item => {
            const roomInfo = roomTypes.find(r => r.id === Number(item.roomId));
            // 'error' geceleri kullanıcı onayıyla (0 ya da elle girilen fiyatla) kaydedilir
            const nightlyRates = item.nightlyRates.map(n => ({
                date: n.date,
                price: round2(toAmount(n.price)),
                source: n.source === 'error' ? 'default' : n.source,
                // Çocuk ücretinin gecede gerçekten eklendiği bilgisi (düzenlemede "dahil" yazısı için)
                ...(n.childTotal > 0 && n.source === 'calendar' ? { base: n.base, childTotal: n.childTotal } : {}),
            }));
            const total = sumNightly(nightlyRates);
            const roomNights = nightlyRates.length;
            const childrenPaid = Number(item.childrenPaid) || 0;
            const childrenFree = Number(item.childrenFree) || 0;
            return {
                id: item.roomId,
                name: roomInfo ? roomInfo.name : 'Bilinmeyen Oda',
                // price = ortalama gecelik; eski kod (PDF/Dashboard/Rapor) price × nights okuduğu için hep yazılır
                price: roomNights > 0 ? round2(total / roomNights) : 0,
                total,
                nightlyRates,
                features: roomInfo ? roomInfo.features : '',
                checkIn: item.checkIn,
                checkOut: item.checkOut,
                nights: roomNights,
                adults: Math.max(1, Number(item.adults) || 1),
                children: childrenPaid + childrenFree, // eski ekranlar toplam çocuk okur
                // Eski teklifte çocuk yaşları hiç seçilmediyse yaş grubu YAZILMAZ: "0-6 yaş" diye kalıcılaşmasın
                ...(item.childAgesUnknown ? {} : {
                    childrenPaid,
                    childrenFree,
                    childFee: roomInfo?.childFee != null ? Number(roomInfo.childFee) : null,
                }),
            };
        });

        const source = offerSources.find(s => s.id === Number(selectedSourceId));

        if (!source) {
            alert("Seçilen kaynak bulunamadı.");
            return; // finally düğmeyi açar
        }

        // Ara toplam kaydedilen odalardan; indirim bunun üzerinden yeniden hesaplanır (ekrandakiyle aynı sonuç)
        const subTotal = round2(roomsData.reduce((acc, r) => acc + r.total, 0));
        const { amount: discountAmount, total: netTotal } = discountEnabled
            ? computeDiscount({ subTotal, type: discountType, value: discount.value })
            : { amount: 0, total: subTotal };

        const offerData = {
            customerName,
            phone,
            email,
            instagram,
            notes,
            currency,
            checkIn: globalCheckIn, checkOut: globalCheckOut, nights: globalNights,
            rooms: roomsData,
            room: roomsData[0], 
            source: { id: source.id, name: source.name },
            // offers.totalPrice = Σ rooms[].total − indirim (müşterinin ödeyeceği tutar)
            totalPrice: netTotal,
            // İndirim kolonları yalnızca indirim varsa (ya da kayıtlı indirim kaldırılıyorsa) gönderilir:
            // indirimsiz teklif, kolonlar veritabanına eklenmeden önce de kaydedilebilsin.
            ...((discountEnabled || hadDiscount) ? {
                discountName: discountEnabled ? discountName.trim() : null,
                discountType: discountEnabled ? discountType : null,
                discountValue: discountEnabled ? discount.value : null,
                discountAmount: discountEnabled ? discountAmount : null,
            } : {}),
            // Yalnızca yeni teklif "Onay Bekliyor" başlar. Düzenlemede status gönderilmez → mevcut durum korunur
            // (eskiden her düzenleme onaylı teklifi beklemeye düşürüyordu).
            // Sezon da yalnızca YENİ teklifte yazılır (Ayarlar'daki aktif sezon); düzenlemede kayıtlı sezon korunur.
            ...(id ? {} : { status: 'Onay Bekliyor', ...(isValidSeason(activeSeason) ? { season: activeSeason } : {}) })
        };

        const userInfo = {
            uid: currentUser.uid,
            email: currentUser.email
        };

        let savedOfferId = id;
        if (id) {
            const response = await fetch(`${API_URL}/offers/${id}`, {
                method: 'PUT',
                headers: jsonAuthHeaders(currentUser?.token),
                body: JSON.stringify({
                    ...offerData,
                    updatedBy: userInfo,
                    updatedAt: new Date()
                })
            });
            await readJsonOrThrow(response, 'Teklif güncellenemedi.');
        } else {
            const response = await fetch(`${API_URL}/offers`, {
                method: 'POST',
                headers: jsonAuthHeaders(currentUser?.token),
                body: JSON.stringify({
                    ...offerData,
                    createdBy: userInfo,
                    createdAt: new Date()
                })
            });
            const result = await readJsonOrThrow(response, 'Teklif kaydedilemedi.');
            if (result?.id === undefined || result?.id === null) {
                // Kayıt büyük olasılıkla oluştu ama numarası bilinmiyor → tekrar kaydetmek mükerrer teklif açar
                throw new Error('Sunucu teklif numarasını döndürmedi. Tekrar kaydetmeden önce Teklifler listesinde teklifin oluşup oluşmadığını kontrol edin.');
            }
            savedOfferId = result.id;
        }

        // İşlem logu bilgi amaçlı: hatası kaydı "başarısız" göstermesin (yoksa kullanıcı tekrar kaydedip mükerrer teklif açardı)
        incrementWrites(2); // Kayıt + Log
        fetch(`${API_URL}/activityLogs`, {
            method: 'POST',
            headers: jsonAuthHeaders(currentUser?.token),
            body: JSON.stringify({
                action: id ? 'Teklif Güncellendi' : 'Teklif Oluşturuldu',
                description: id ? `${customerName} için teklif güncellendi.` : `${customerName} için yeni teklif oluşturuldu.`,
                performedBy: userInfo
            })
        }).catch(() => {});

        // Panelde ve PDF'te kullanılan teklif: kaydedilen veri + sunucunun verdiği numara (PDF'teki tarih = bugün)
        savedOffer = { ...offerData, id: savedOfferId, createdAt: new Date() };
    } catch (error) {
        console.error("Teklif kaydetme hatası:", error);
        setSaveError(errorText(error, 'Teklif kaydedilemedi.'));
    } finally {
        if (!savedOffer) savingRef.current = false; // başarısızsa düzeltip yeniden denenebilsin
        setLoading(false);
    }
    if (!savedOffer) return;

    // Kaydedilen teklif başka bir sezona yazıldıysa görünüm o sezona geçsin; yoksa listede hiç görünmez
    const savedSeason = id ? offerSavedSeason : activeSeason;
    if (isValidSeason(savedSeason) && selectedSeason !== ALL_SEASONS && selectedSeason !== savedSeason) {
        setSelectedSeason(savedSeason);
    }
    setDirty(false);
    setReady({ offer: savedOffer, pdf: 'uploading', pdfError: '' });
    refreshData('offers'); // liste, takvim ve müşteri kartı güncel teklifi görsün

    // PDF OLUŞTURMA VE SUNUCUYA YÜKLEME — paylaşım düğmeleri yalnızca bu adım başarılı olunca açılır
    await runPdfStep(savedOffer);
  };

  const discountView = calculateDiscount();

  return (
    <div>
      <div className="max-w-6xl mx-auto pb-10">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6">
        <button type="button" onClick={handleBack} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" aria-label="Teklif listesine dön"><ArrowLeft size={24} /></button>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">{id ? 'Teklifi Düzenle' : 'Yeni Rezervasyon Teklifi'}</h1>
        {/* Sezon bilgi amaçlı: yeni teklif Ayarlar'daki aktif sezona, düzenlenen teklif kendi sezonunda kalır */}
        {(id ? offerSavedSeason : activeSeason) && (
          <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200">
            {id ? offerSavedSeason : activeSeason} Sezonu
          </span>
        )}
      </div>
      {loadError && (
        <div role="alert" className="mb-6 p-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm flex items-start gap-2">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <span>{loadError} Bu ekrandan kaydedilemez; Teklifler listesine dönüp tekrar deneyin.</span>
        </div>
      )}
      {lookupWarning && !loadError && (
        <div role="alert" className="mb-6 p-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-sm flex items-start gap-2">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <span>{lookupWarning}</span>
        </div>
      )}
      {id && openedAsCopy && !loadError && !ready && (
        <div role="status" className="mb-6 px-4 py-3 rounded-lg border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/30 text-sky-800 dark:text-sky-200 text-sm flex items-start gap-2">
          <Copy size={16} className="shrink-0 mt-0.5" />
          <span className="min-w-0 break-words">Bu bir kopya teklif. Bilgileri kontrol edip kaydedince PDF'i hazırlanır.</span>
        </div>
      )}
      {/* Alanlardaki her değişiklik (input/select/textarea) formda kabarcıklanır → kaydedilmemiş değişiklik */}
      <form onSubmit={handleSubmit} onChange={markDirty} className="space-y-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow grid grid-cols-1 md:grid-cols-2 gap-6 border border-gray-100 dark:border-gray-700">
            <input type="text" required className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Müşteri Adı Soyadı" />
            <input type="tel" className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Telefon Numarası" />
            <input type="email" className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400" value={email} onChange={e => setEmail(e.target.value)} placeholder="E-posta Adresi" />
            <input type="text" className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400" value={instagram} onChange={e => setInstagram(e.target.value)} placeholder="Instagram Kullanıcı Adı" />
            {customer && (
                <div className="md:col-span-2 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 p-3 sm:p-4 text-sm" aria-live="polite">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                            <p className="font-semibold text-blue-900 dark:text-blue-100 flex items-start gap-1.5">
                                <History size={16} className="shrink-0 mt-0.5" />
                                <span>Bu müşteriye daha önce {customerSummaryText(customer.summary)}</span>
                            </p>
                            {customer.via.length > 0 && (
                                <p className="mt-0.5 text-xs text-blue-700 dark:text-blue-300">Eşleşen: {customer.via.join(', ')}</p>
                            )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            {fillCandidates.length > 0 && (
                                <button type="button" onClick={handleFillFromLast}
                                    title={`En yeni tekliften boş alanlara yazılır: ${fillCandidates.map(([label]) => label).join(', ')}`}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-white dark:bg-gray-800 border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50">
                                    <UserCheck size={14} /> Bilgileri doldur
                                </button>
                            )}
                            {customer.pending && (
                                <button type="button" onClick={handleOpenPending}
                                    className="inline-flex items-center gap-1 px-1 py-1.5 text-xs font-semibold text-blue-700 dark:text-blue-300 hover:underline">
                                    <ExternalLink size={14} /> Bekleyen teklifi aç
                                </button>
                            )}
                        </div>
                    </div>
                    <ul className="mt-2 divide-y divide-blue-100 dark:divide-blue-800/60">
                        {customer.list.slice(0, 3).map(o => {
                            const otherName = o.customerName && foldTr(o.customerName) !== foldTr(customerName) ? o.customerName : '';
                            return (
                                <li key={o.id} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                                    <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatDateTR(o.createdAt) || '—'}</span>
                                    {otherName && <span className="text-gray-700 dark:text-gray-200 break-words min-w-0">{otherName}</span>}
                                    <span className="font-medium text-gray-800 dark:text-gray-100 break-words min-w-0">{offerRoomsText(o) || 'Oda bilgisi yok'}</span>
                                    {formatStay(o) && <span className="text-gray-600 dark:text-gray-300">{formatStay(o)}</span>}
                                    <span className="ml-auto flex items-center gap-2">
                                        {canSeeMoney && <span className="font-semibold text-gray-800 dark:text-gray-100 whitespace-nowrap">{offerMoneyText(o)}</span>}
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${STATUS_BADGE[o.status] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'}`}>
                                            {o.status || 'Durum yok'}
                                        </span>
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                    {customer.list.length > 3 && (
                        <p className="mt-1 text-xs text-blue-700 dark:text-blue-300">ve {customer.list.length - 3} eski teklif daha</p>
                    )}
                </div>
            )}
            <select required className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white" value={selectedSourceId} onChange={e => setSelectedSourceId(e.target.value)}>
                <option value="">Teklif Kaynağını Seçiniz...</option>
                {offerSources.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <div className="relative">
                <label className="absolute -top-2 left-2 bg-white dark:bg-gray-800 px-1 text-xs text-gray-500 dark:text-gray-400">Para Birimi</label>
                <select 
                    className="w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white" 
                    value={currency} 
                    onChange={e => handleCurrencyChange(e.target.value)} 
                >
                    <option value="TL">Türk Lirası (TL)</option>
                    <option value="USD">Amerikan Doları (USD)</option>
                    <option value="EUR">Euro (EUR)</option>
                    <option value="GBP">İngiliz Sterlini (GBP)</option>
                </select>
            </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-100 dark:border-gray-700">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Notlar</label>
            <textarea 
                className="w-full p-2 border rounded h-24 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400" 
                value={notes} 
                onChange={e => setNotes(e.target.value)} 
                placeholder="Teklif ile ilgili özel notlar..." 
            />
        </div>

        {/* Oda Seçimi ve Fiyatlandırma */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-100 dark:border-gray-700">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Oda Seçimi ve Fiyatlandırma</h3>
                <button type="button" onClick={handleAddRoom} className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"><Plus size={16}/> Oda Ekle</button>
            </div>
            {!autoPricing && (
                <div className="mb-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-sm flex items-start gap-2">
                    <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                    <span>
                        Fiyat takvimi TL cinsindendir. {currency} tekliflerde yeni gecelere otomatik fiyat yazılmaz; gecelik fiyatları ya da oda toplamını elle girin.
                        {selectedRooms.some(r => r.nightlyRates.some(n => n.source !== 'manual')) && ' Aşağıdaki bazı fiyatlar TL takviminden geldi — lütfen güncelleyin.'}
                    </span>
                </div>
            )}
            {selectedRooms.map((roomItem, index) => {
                const roomNights = calculateNights(roomItem.checkIn, roomItem.checkOut);
                const roomTotal = sumNightly(roomItem.nightlyRates);
                const roomType = roomTypes.find(r => r.id === Number(roomItem.roomId));
                const defaultCount = roomItem.nightlyRates.filter(n => n.source === 'default').length;
                const missingCount = roomItem.nightlyRates.filter(n => n.source === 'missing').length;
                const showBreakdown = !!roomItem.roomId && roomNights > 0;
                const overCapacity = roomType?.adultCapacity && Number(roomItem.adults) > Number(roomType.adultCapacity);
                const childFee = Number(roomType?.childFee) || 0;
                const paidKids = Number(roomItem.childrenPaid) || 0;
                // "Çocuk ücreti dahil" yalnızca ücret HER gecede gerçekten eklendiyse yazılır
                const nightsWithChildFee = roomItem.nightlyRates.filter(n => n.childTotal > 0).length;
                const childFeeIncluded = paidKids > 0 && roomItem.nightlyRates.length > 0 && nightsWithChildFee === roomItem.nightlyRates.length;
                const childFeeMissingNights = paidKids > 0 && childFee > 0 && !roomItem.pricing ? roomItem.nightlyRates.length - nightsWithChildFee : 0;
                const selectCls = 'w-full p-2 border rounded dark:bg-gray-600 dark:border-gray-500 dark:text-white';

                return (
                <div key={roomItem.lineId} className="mb-4 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-700/40">
                <div className="flex flex-col md:flex-row gap-3 items-end">
                    <div className="w-full md:flex-1">
                        <label className="block text-xs text-gray-500 dark:text-gray-300 mb-1">Oda Tipi</label>
                        <select
                            className={selectCls}
                            value={roomItem.roomId}
                            onChange={e => handleRoomChange(index, 'roomId', e.target.value)}
                            required
                        >
                            <option value="">Oda Seçiniz...</option>
                            {roomTypes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                    </div>
                    <div className="w-full md:w-20">
                        <label className="block text-xs text-gray-500 dark:text-gray-300 mb-1" title="13 yaş ve üstü">Yetişkin</label>
                        <select className={selectCls} value={roomItem.adults} onChange={e => handleRoomChange(index, 'adults', e.target.value)}>
                            {ADULT_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                    </div>
                    <div className="w-full md:w-24">
                        <label className="block text-xs text-gray-500 dark:text-gray-300 mb-1" title="7-12 yaş: oda tipinin çocuk ücreti her gece eklenir">Çocuk 7-12</label>
                        <select className={selectCls} value={roomItem.childrenPaid} onChange={e => handleRoomChange(index, 'childrenPaid', e.target.value)}>
                            {CHILD_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                    </div>
                    <div className="w-full md:w-24">
                        <label className="block text-xs text-gray-500 dark:text-gray-300 mb-1" title="0-6 yaş: ücretsiz">Çocuk 0-6</label>
                        <select className={selectCls} value={roomItem.childrenFree} onChange={e => handleRoomChange(index, 'childrenFree', e.target.value)}>
                            {CHILD_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                    </div>
                    <div className="w-full md:w-36">
                        <label className="block text-xs text-gray-500 dark:text-gray-300 mb-1">Giriş</label>
                        <input
                            type="date"
                            min="2000-01-01"
                            max="2100-12-31"
                            className={selectCls}
                            value={roomItem.checkIn}
                            onChange={e => handleRoomChange(index, 'checkIn', e.target.value)}
                            required
                        />
                    </div>
                    <div className="w-full md:w-36">
                        <label className="block text-xs text-gray-500 dark:text-gray-300 mb-1">Çıkış</label>
                        <input
                            type="date"
                            min="2000-01-01"
                            max="2100-12-31"
                            className={selectCls}
                            value={roomItem.checkOut}
                            onChange={e => handleRoomChange(index, 'checkOut', e.target.value)}
                            required
                        />
                    </div>
                    <div className="w-full md:w-40">
                        <label className="block text-xs text-gray-500 dark:text-gray-300 mb-1">Oda Toplamı ({currency})</label>
                        <LineTotalInput
                            total={roomTotal}
                            disabled={!showBreakdown || roomItem.pricing || roomItem.nightlyRates.length === 0}
                            onCommit={value => handleLineTotalCommit(index, value)}
                        />
                    </div>
                    {selectedRooms.length > 1 && (
                        <div className="w-full md:w-auto flex justify-end md:block">
                            <button type="button" onClick={() => handleRemoveRoom(index)} className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded" aria-label="Odayı kaldır"><Trash2 size={18} /></button>
                        </div>
                    )}
                </div>

                {(overCapacity || roomItem.childAgesUnknown || roomItem.occupancyStale || childFeeMissingNights > 0 || (paidKids > 0 && roomType && !childFee && autoPricing)) && (
                    <div className="mt-2 space-y-1">
                        {overCapacity && (
                            <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                                <AlertTriangle size={12} /> {roomType.name} en fazla {roomType.adultCapacity} yetişkin alır.
                            </p>
                        )}
                        {roomItem.occupancyStale && (
                            <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                                <AlertTriangle size={12} /> Kişi sayısı değiştirildi ama gece fiyatları güncellenmedi. Fiyatları elle düzeltin ya da "Takvimden yeniden hesapla"ya basın.
                            </p>
                        )}
                        {childFeeMissingNights > 0 && (
                            <p className="text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1">
                                <AlertTriangle size={12} /> {childFeeMissingNights} gecenin fiyatına 7-12 yaş çocuk ücreti eklenmedi (elle/kayıtlı fiyat ya da takvimde fiyat yok). Gerekirse çocuk başı gecelik {fmt(childFee)} TL'yi elle ekleyin.
                            </p>
                        )}
                        {roomItem.childAgesUnknown && (
                            <p className="text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1">
                                <AlertTriangle size={12} /> Eski teklif: çocukların yaş grubu kayıtlı değil, hepsi 0-6 yaş olarak gösterildi. 7-12 yaş varsa düzeltin.
                            </p>
                        )}
                        {paidKids > 0 && roomType && !childFee && autoPricing && (
                            <p className="text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1">
                                <AlertTriangle size={12} /> {roomType.name} için 7-12 yaş çocuk ücreti tanımlı değil; çocuk ücreti eklenmedi.
                            </p>
                        )}
                    </div>
                )}

                {showBreakdown && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                            <p className="text-xs text-gray-600 dark:text-gray-300">
                                {roomItem.pricing ? 'Fiyatlar takvimden alınıyor…' : roomItem.nightlyRates.length === 0 ? null : (
                                    <>
                                        <strong>{roomNights}</strong> gece · ort.{' '}
                                        <strong>{round2(roomTotal / roomNights).toLocaleString('tr-TR')} {currency}</strong> / gece
                                        {childFeeIncluded && (
                                            <span className="text-gray-500 dark:text-gray-400"> · her gece {paidKids} çocuk × {fmt(childFee)} TL dahil</span>
                                        )}
                                    </>
                                )}
                            </p>
                            <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-500 dark:text-gray-400">
                                <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm border border-gray-400 bg-white dark:bg-gray-600" />takvim</span>
                                <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm border border-amber-400 bg-amber-50" />takvimde yok (0)</span>
                                <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm border border-violet-400 bg-violet-50" />elle</span>
                                <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm border border-dashed border-red-500 bg-red-50" />fiyat yok</span>
                                <button
                                    type="button"
                                    onClick={() => handleRecalculate(index)}
                                    disabled={!autoPricing || roomItem.pricing}
                                    className="ml-1 flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
                                    title={autoPricing ? 'Tüm geceleri fiyat takviminden yeniden hesapla' : 'Fiyat takvimi yalnızca TL tekliflerde kullanılır'}
                                >
                                    <RotateCcw size={12} /> Takvimden yeniden hesapla
                                </button>
                            </div>
                        </div>

                        {!roomItem.pricing && (
                            <div className="grid grid-cols-[repeat(auto-fill,minmax(104px,1fr))] gap-2">
                                {roomItem.nightlyRates.map(night => (
                                    <label key={night.date} className="block" title={nightTitle(night)}>
                                        <span className="block text-[11px] text-gray-500 dark:text-gray-400 mb-0.5">{formatNightLabel(night.date)}</span>
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            autoComplete="off"
                                            value={night.price}
                                            placeholder={night.source === 'missing' ? 'fiyat yok' : ''}
                                            aria-label={`${formatNightLabel(night.date)} gecelik fiyat`}
                                            onChange={e => handleNightPriceChange(index, night.date, e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                                            className={`w-full px-2 py-1.5 text-sm text-right border rounded dark:text-white ${SOURCE_STYLES[night.source] || SOURCE_STYLES.manual}`}
                                        />
                                    </label>
                                ))}
                            </div>
                        )}

                        {missingCount > 0 && !roomItem.pricing && (
                            <p className="mt-2 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                                <AlertTriangle size={12} /> {missingCount} gece için fiyat takviminde {roomItem.adults} yetişkin fiyatı yok. Fiyatı elle girin ya da yetişkin sayısını kontrol edin.
                            </p>
                        )}
                        {defaultCount > 0 && !roomItem.pricing && (
                            <p className="mt-2 text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1">
                                <AlertTriangle size={12} /> {defaultCount} gece fiyat takviminde tanımlı dönemin dışında; bu gecelerin fiyatı 0 yazıldı. Gerekirse elle girin.
                            </p>
                        )}
                        {/* Hata; ya gece listesi kurulamadıysa (aşırı uzun aralık) ya da hâlâ kırmızı gece varsa görünür */}
                        {roomItem.pricingError && !roomItem.pricing &&
                          (roomItem.nightlyRates.length === 0 || roomItem.nightlyRates.some(n => n.source === 'error')) && (
                            <p className="mt-2 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                                <AlertTriangle size={12} /> {roomItem.pricingError}
                            </p>
                        )}
                    </div>
                )}
                </div>
            )})}
        </div>

        {/* İndirim — teklif başına tek, tüm odaların toplamına uygulanır */}
        {(() => {
            const d = discountView;
            const valueEmpty = discountInput.trim() === '';
            const inputCls = 'w-full p-2 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400';
            const typeBtn = (value, label) => (
                <button type="button" onClick={() => { setDiscountType(value); markDirty(); }} aria-pressed={discountType === value}
                    className={`flex-1 px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${discountType === value
                        ? 'bg-blue-600 text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'}`}>
                    {label}
                </button>
            );
            return (
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow border border-gray-100 dark:border-gray-700">
                    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                        <input type="checkbox" className="h-4 w-4" checked={discountEnabled} onChange={e => setDiscountEnabled(e.target.checked)} />
                        <Tag size={18} className="text-gray-500 dark:text-gray-400" />
                        <span className="text-lg font-semibold text-gray-700 dark:text-gray-200">İndirim uygula</span>
                    </label>
                    {discountEnabled && (
                        <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-4">
                                <div className="sm:col-span-2">
                                    <label htmlFor="discountName" className="block text-xs text-gray-500 dark:text-gray-300 mb-1">İndirim Adı (PDF'te bu adla görünür)</label>
                                    <input id="discountName" type="text" list="discount-name-options" maxLength={DISCOUNT_NAME_MAX}
                                        className={inputCls} value={discountName} onChange={e => setDiscountName(e.target.value)} placeholder={DEFAULT_DISCOUNT_NAME} />
                                    <datalist id="discount-name-options">
                                        {DISCOUNT_NAME_SUGGESTIONS.map(n => <option key={n} value={n} />)}
                                    </datalist>
                                </div>
                                <div>
                                    <span className="block text-xs text-gray-500 dark:text-gray-300 mb-1">İndirim Türü</span>
                                    <div className="flex rounded border border-gray-300 dark:border-gray-600 overflow-hidden" role="group" aria-label="İndirim türü">
                                        {typeBtn('percentage', 'Yüzde (%)')}
                                        {typeBtn('amount', `Tutar (${currency})`)}
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="discountValue" className="block text-xs text-gray-500 dark:text-gray-300 mb-1">
                                        {discountType === 'percentage' ? 'İndirim Oranı (%)' : `İndirim Tutarı (${currency})`}
                                    </label>
                                    <div className="relative">
                                        {/* Metin: "5.000" Türkçe biçimde doğru okunur (type=number 5 okurdu) */}
                                        <input id="discountValue" type="text" inputMode="decimal" autoComplete="off"
                                            className={`${inputCls} pr-12 ${d.error && !valueEmpty ? 'border-red-400 dark:border-red-500' : ''}`}
                                            value={discountInput} onChange={e => setDiscountInput(e.target.value)}
                                            placeholder={discountType === 'percentage' ? 'ör. 10' : 'ör. 5.000'} />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
                                            {discountType === 'percentage' ? '%' : currency}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            {/* Değer henüz yazılmadıysa ipucu gri; geçersiz değer kırmızı; geçerliyse düşülecek tutar yeşil */}
                            <p className={`mt-3 text-sm ${!d.error ? 'text-green-700 dark:text-green-400' : valueEmpty ? 'text-gray-500 dark:text-gray-400' : 'text-red-600 dark:text-red-400'}`}>
                                {d.error
                                    ? d.error
                                    : `${discountLabel({ name: discountName.trim(), type: discountType, value: d.value })}: -${formatAmount(d.amount)} ${currency}`}
                            </p>
                        </>
                    )}
                </div>
            );
        })()}

        <div className="bg-blue-50 dark:bg-blue-900/50 p-6 rounded-lg flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="w-full md:w-auto">
                <div className="flex flex-col gap-2">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-blue-800 dark:text-blue-200">
                        <span>Ara Toplam: <strong>{formatAmount(calculateSubTotal())} {currency}</strong></span>
                        {discountView.amount > 0 && (
                            <span className="text-green-700 dark:text-green-300">
                                {discountLabel({ name: discountName.trim(), type: discountType, value: discountView.value })}: <strong>-{formatAmount(discountView.amount)} {currency}</strong>
                            </span>
                        )}
                        <span>
                            Genel Süre: <span className="font-bold">
                            {calculateNights(
                                selectedRooms.length > 0 ? selectedRooms.reduce((min, r) => r.checkIn && r.checkIn < min ? r.checkIn : min, selectedRooms[0].checkIn) : '',
                                selectedRooms.length > 0 ? selectedRooms.reduce((max, r) => r.checkOut && r.checkOut > max ? r.checkOut : max, selectedRooms[0].checkOut) : ''
                            )} Gece</span>
                        </span>
                    </div>
                    
                    <p className="text-2xl font-bold text-blue-900 dark:text-white mt-1">
                        Toplam: {formatAmount(calculateTotalPrice())} {currency}
                    </p>
                </div>
            </div>
            <button type="submit" disabled={loading || !offerReady || !!ready} className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 flex items-center gap-2 w-full md:w-auto justify-center disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-blue-600">
                {loading ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}
                {loading ? 'Kaydediliyor...' : (!offerReady && !loadError) ? 'Teklif yükleniyor...' : (id ? 'Güncelle' : 'Teklifi Oluştur')}
            </button>
        </div>
        {saveError && (
            <div role="alert" className="p-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm flex items-start gap-2">
                <AlertTriangle size={18} className="shrink-0 mt-0.5" />
                <span className="break-words"><strong>Teklif kaydedilemedi:</strong> {saveError}</span>
            </div>
        )}
      </form>
      </div>
      {ready && (
        <OfferReadyPanel
          offer={ready.offer}
          pdfState={ready.pdf}
          pdfError={ready.pdfError}
          onRetryPdf={() => runPdfStep(ready.offer)}
          onExit={() => navigate('/offers')}
          shareCtx={{ messages: messageSettings, logos: settings, bank: settings.bank, origin: window.location.origin }}
          token={currentUser?.token}
          onShared={() => incrementWrites(1)}
        />
      )}
    </div>
  );
}
